import { type ReactNode, useCallback, useRef, useState } from 'react';
import { cn } from '~/lib/utils';
import { useStore } from '~/store';
import { loadSource } from './actions';
import { saveSourceBlob } from './idb';
import { blobToImageData, IMAGE_FILE_ACCEPT, isImageFile } from './image-utils';

export function SourceDropZone({ children }: { children: ReactNode }) {
  const [over, setOver] = useState(false);
  const hasSource = useStore((s) => s.source.imageData !== null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onFile = useCallback(async (file: File) => {
    const [bytes, imageData] = await Promise.all([file.arrayBuffer(), blobToImageData(file)]);
    await loadSource({ fileName: file.name, bytes, imageData });
    void saveSourceBlob(file);
  }, []);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop zone requires drag handlers on a div
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files[0];
        if (isImageFile(file)) void onFile(file);
      }}
      onClick={hasSource ? undefined : () => inputRef.current?.click()}
      onKeyDown={
        hasSource
          ? undefined
          : (e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              inputRef.current?.click();
            }
      }
      role={hasSource ? undefined : 'button'}
      tabIndex={hasSource ? undefined : 0}
      className={cn(
        'relative h-full min-w-0 flex-1 transition-colors',
        !hasSource && 'cursor-pointer',
        over && 'bg-primary/5 ring-2 ring-primary/40',
      )}
    >
      {children}
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_FILE_ACCEPT}
        aria-label="Load image"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (isImageFile(file)) void onFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
